import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent } from "../ui/sheet";
import { DockHeader } from "./DockHeader";
import { ChatDockPanel } from "./ChatDockPanel";
import { useProChatSocket } from "../../hooks/useProChatSocket";
import { useProChatStore } from "../../stores/proChat";
import { useProToken } from "../../hooks/useProToken";
import { useAssistantAllowance } from "../../hooks/useProData";
import { BILLING_SETTINGS_ROUTE } from "../../lib/entitlement-errors";
import { fetchActiveProConversation } from "../../lib/pro-history";
import { useCopilotPreviewFixture } from "../../hooks/useCopilotPreviewFixture";
import {
  COPILOT_DOCK_BREAKPOINT,
  useMediaQuery,
} from "../../hooks/useMediaQuery";

// `useProToken` returns an async resolver rather than a live token value
// (see `useCampaign.ts` for the same pattern); this small hook resolves it
// once per identity change into a snapshot the WS hook can consume.
function useResolvedProToken(): string | null {
  const getToken = useProToken();
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = await getToken();
      if (!cancelled) setToken(t);
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);
  return token;
}

function clientProfileIdFromPath(pathname: string): string | undefined {
  const match = /^\/clients\/([^/]+)/.exec(pathname);
  return match ? match[1] : undefined;
}

// ProChatDock — the copilot surface (plan 188 W2-M8, task 802):
//   - >=1280px: a docked ~404px right panel. ProLayout renders this as a
//     real flex column (never absolutely positioned), so the CRM content
//     reflows beside it rather than being occluded (scenario 674).
//   - <1280px: an overlay bottom-sheet drawer with a scrim (scenario 679),
//     built on the shared `Sheet` primitive.
// The sole opener lives in the ProLayout header (HeaderNav's "Sill" pill);
// this component only renders the open dock/drawer body.
export function ProChatDock({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const isDesktop = useMediaQuery(COPILOT_DOCK_BREAKPOINT);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const getToken = useProToken();
  const token = useResolvedProToken();
  const clearMessages = useProChatStore((s) => s.clearMessages);
  const loadHistory = useProChatStore((s) => s.loadHistory);
  const allowance = useAssistantAllowance();
  const onUpgrade = () => navigate(BILLING_SETTINGS_ROUTE);
  // The resolved `token` snapshot gates WHEN to connect (and feeds the REST
  // history fetch below); the handshake itself mints a fresh token per
  // attempt via `getToken` — a snapshot replayed on reconnect is expired
  // within ~60s (Clerk JWT TTL) and permanently dead-ends the dock.
  const socket = useProChatSocket({ getToken, enabled: token !== null });

  // Rehydrate the most recent conversation once per page load so a refresh
  // doesn't drop the current chat. Only runs when the module-level store is
  // empty (fresh load) — never clobbers an in-session conversation, and a
  // "New" (clearMessages) intentionally leaves it empty without re-fetching
  // because the guard ref has already fired.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!token || hydratedRef.current) return;
    if (useProChatStore.getState().messages.length > 0) {
      hydratedRef.current = true;
      return;
    }
    hydratedRef.current = true;
    const controller = new AbortController();
    fetchActiveProConversation(token, controller.signal)
      .then((history) => {
        // Guard against a race with an in-flight turn started before hydration.
        if (useProChatStore.getState().messages.length > 0) return;
        if (history.messages.length === 0) return;
        loadHistory({
          conversationId: history.conversationId,
          messages: history.messages,
          dockItems: [],
        });
      })
      .catch(() => {
        // Best-effort — a hydration miss just leaves the empty state.
      });
    return () => controller.abort();
  }, [token, loadHistory]);
  // IS_PREVIEW-only: seeds a sample conversation from `?copilotDemo=<variant>`
  // for design-fidelity screenshots (task 808). No-op on the authed path.
  useCopilotPreviewFixture();

  const clientProfileId = clientProfileIdFromPath(pathname);

  function handleExpand() {
    navigate("/assistant", { state: { from: pathname } });
  }

  if (!open) return null;

  if (isDesktop) {
    return (
      <aside
        data-testid="copilot-dock-desktop"
        // Both offsets come from ProLayout (the shell owns them; this dock must
        // never re-derive them). --pro-header-h already accounts for the
        // Engagement sub-nav, and --pro-banner-h for a banner sitting above
        // this flex row — without the latter the dock overflows the viewport
        // bottom and takes the "Ask Sill" composer below the fold. The
        // fallbacks apply only when rendered outside ProLayout.
        className="sticky top-[var(--pro-header-h,60px)] flex h-[calc(100vh-var(--pro-header-h,60px)-var(--pro-banner-h,0px))] w-[404px] shrink-0 flex-col border-l border-border-soft bg-card"
      >
        <DockHeader
          onExpand={handleExpand}
          onNewConversation={clearMessages}
          onClose={onClose}
        />
        <ChatDockPanel
          socket={socket}
          getToken={getToken}
          pathname={pathname}
          clientProfileId={clientProfileId}
          allowance={allowance}
          onUpgrade={onUpgrade}
        />
      </aside>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="bottom"
        data-testid="copilot-dock-drawer"
        className="flex h-[85vh] flex-col rounded-t-2xl"
      >
        <div
          aria-hidden
          className="mx-auto mt-1.5 h-1 w-10 shrink-0 rounded-full bg-border"
        />
        <DockHeader
          onExpand={handleExpand}
          onNewConversation={clearMessages}
          onClose={onClose}
        />
        <ChatDockPanel
          socket={socket}
          getToken={getToken}
          pathname={pathname}
          clientProfileId={clientProfileId}
          allowance={allowance}
          onUpgrade={onUpgrade}
        />
      </SheetContent>
    </Sheet>
  );
}
